import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';

const ImageBlock = React.memo(({ data, isSelected, onUpdate, previewMode, blockId, onSelectBlock }) => {
  const fileInputRef = useRef(null);
  
  // Мемоизируем данные блока для предотвращения лишних рендеров
  const memoizedData = useMemo(() => ({
    src: data?.src || '',
    alt: data?.alt || 'Image description',
    width: data?.width || '100%',
    height: data?.height || 'auto',
    alignment: data?.alignment || 'center',
    textAlign: data?.textAlign || 'center',
    borderRadius: data?.borderRadius || 0,
    link: data?.link || '',
    marginTop: data?.marginTop !== undefined ? data.marginTop : 10,
    marginBottom: data?.marginBottom !== undefined ? data.marginBottom : 10,
    padding: data?.padding !== undefined ? data.padding : 0,
    backgroundColor: data?.backgroundColor || 'transparent',
    opacity: data?.opacity || 1,
    boxShadow: data?.boxShadow || 'none',
  }), [data]);

  // Прямая привязка события onChange (оптимизировано)
  useEffect(() => {
    const inputElement = fileInputRef.current;
    if (inputElement) {
      const handleChange = (e) => {
        handleFileUpload(e);
      };
      
      inputElement.addEventListener('change', handleChange);
      
      return () => {
        inputElement.removeEventListener('change', handleChange);
      };
    }
    // Mount-only listener; rebinding on every handleFileUpload change is unnecessary
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Мемоизируем функцию получения адаптивной ширины
  const getResponsiveWidth = useCallback(() => {
    if (previewMode === 'mobile') {
      return '100%';
    }
    return memoizedData.width;
  }, [previewMode, memoizedData.width]);

  // Мемоизируем стили контейнера
  const containerStyles = useMemo(() => ({
    textAlign: memoizedData.alignment || memoizedData.textAlign,
    marginTop: `${memoizedData.marginTop}px`,
    marginBottom: `${memoizedData.marginBottom}px`,
    padding: `${memoizedData.padding}px`,
    backgroundColor: memoizedData.backgroundColor,
    opacity: memoizedData.opacity,
    boxShadow: memoizedData.boxShadow !== 'none' ? memoizedData.boxShadow : 'none',
    border: isSelected ? '1px dashed #007bff' : 'none',
    borderRadius: isSelected ? '4px' : 0,
    minHeight: memoizedData.src ? 'auto' : '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: memoizedData.alignment || memoizedData.textAlign,
    cursor: 'pointer',
  }), [memoizedData, isSelected]);

  // Мемоизируем стили изображения
  const imageStyles = useMemo(() => ({
    width: getResponsiveWidth(),
    height: memoizedData.height,
    borderRadius: `${memoizedData.borderRadius}px`,
    maxWidth: '100%',
    display: 'block',
    objectFit: 'cover',
  }), [getResponsiveWidth, memoizedData.height, memoizedData.borderRadius]);

  // Мемоизируем стили плейсхолдера
  const placeholderStyles = useMemo(() => ({
    width: '100%',
    height: '100px',
    backgroundColor: '#e9ecef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#666',
    fontSize: '14px',
    borderRadius: `${memoizedData.borderRadius}px`,
  }), [memoizedData.borderRadius]);

  // Функция преобразования изображения в base64 БЕЗ СЖАТИЯ для 100% качества
  const processImageToBase64 = useCallback((file) => {
    return new Promise((resolve) => {
      // Для максимального качества просто читаем файл как base64 без canvas обработки
      const reader = new FileReader();
      
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      
      reader.onerror = () => {
        console.error('Error reading image file');
        resolve(null);
      };
      
      // Читаем файл как data URL (base64) - это сохраняет 100% качества
      reader.readAsDataURL(file);
    });
  }, []);

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    
    if (file) {
      // Проверяем тип файла
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Увеличиваем лимит размера до 10MB для высококачественных изображений
      if (file.size > 10 * 1024 * 1024) {
        alert('File size should be less than 10MB');
        return;
      }
      
      try {
        // Показываем индикатор загрузки
        const loadingData = { 
          src: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
              <rect width="100" height="100" fill="#f0f0f0"/>
              <text x="50" y="50" text-anchor="middle" dy="0.3em" font-family="Arial" font-size="12" fill="#666">
                Loading...
              </text>
            </svg>
          `),
          alt: 'Loading...'
        };
        
        if (onUpdate) {
          onUpdate(loadingData);
        }
        
        // Асинхронно преобразуем изображение в base64 БЕЗ СЖАТИЯ для максимального качества
        setTimeout(async () => {
          try {
            const originalDataUrl = await processImageToBase64(file);
            
            if (originalDataUrl) {
              const updatedData = { 
                src: originalDataUrl,
                alt: file.name.split('.')[0]
              };
              
              if (onUpdate) {
                // Обновляем только свойства изображения, сохраняя остальные
                onUpdate(updatedData);
              }
              
              // Обновляем selectedBlock если этот блок выбран
              if (onSelectBlock && blockId) {
                onSelectBlock({
                  id: blockId,
                  type: 'image',
                  data: { ...memoizedData, ...updatedData }
                });
              }
            } else {
              // Если преобразование не удалось, возвращаем пустое состояние
              if (onUpdate) {
                onUpdate({ src: '', alt: 'Failed to load image' });
              }
              alert('Failed to process image. Please try another file.');
            }
          } catch (error) {
            console.error('Error processing image:', error);
            if (onUpdate) {
              onUpdate({ src: '', alt: 'Error loading image' });
            }
            alert('Error processing image. Please try again.');
          }
        }, 50); // Небольшая задержка для предотвращения блокировки UI
        
      } catch (error) {
        console.error('Error handling file upload:', error);
        alert('Error uploading file. Please try again.');
      }
    }
    
    // Очищаем input для возможности повторного выбора того же файла
    event.target.value = '';
  }, [onUpdate, onSelectBlock, blockId, memoizedData, processImageToBase64]);

  const handleUrlPrompt = useCallback(() => {
    const url = prompt('Enter image URL:', memoizedData.src || '');
    if (url !== null) {
      const updatedData = { src: url };
      
      if (onUpdate) {
        // Обновляем только URL изображения, сохраняя остальные свойства
        onUpdate(updatedData);
      }
      
      // Обновляем selectedBlock если этот блок выбран
      if (onSelectBlock && blockId) {
        onSelectBlock({
          id: blockId,
          type: 'image',
          data: { ...memoizedData, ...updatedData }
        });
      }
    }
  }, [onUpdate, onSelectBlock, blockId, memoizedData]);

  // Мемоизируем компонент изображения
  const ImageComponent = useMemo(() => {
    if (!memoizedData.src) {
      return (
        <Box sx={placeholderStyles}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              Add an image
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ 
              fontSize: '12px',
              fontStyle: 'italic',
              opacity: 0.7
            }}>
              💡 Maximum image upload size: 10 MB (100% quality preserved)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUpload />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (fileInputRef.current) {
                    fileInputRef.current.click();
                  }
                }}
                sx={{ fontSize: '12px' }}
              >
                Upload File
              </Button>
            </Box>
          </Box>
        </Box>
      );
    }

    const imageElement = (
      <img
        src={memoizedData.src}
        alt={memoizedData.alt}
        style={imageStyles}
        onError={(e) => {
          e.target.style.backgroundColor = '#f8d7da';
          e.target.alt = 'Image failed to load';
        }}
        onDoubleClick={handleUrlPrompt}
      />
    );

    if (memoizedData.link) {
      return (
        <a href={memoizedData.link} style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
          {imageElement}
        </a>
      );
    }

    return imageElement;
  }, [memoizedData.src, memoizedData.alt, memoizedData.link, placeholderStyles, imageStyles, handleUrlPrompt]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (onSelectBlock && blockId) {
      onSelectBlock({
        id: blockId,
        type: 'image',
        data: memoizedData
      });
    }
  }, [onSelectBlock, blockId, memoizedData]);

  return (
    <Box sx={containerStyles} onClick={handleClick}>
      {ImageComponent}
      
      {/* Выносим input за пределы сложной структуры */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        onClick={(e) => {
          // Убеждаемся, что событие не блокируется
          e.stopPropagation();
        }}
      />
    </Box>
  );
});

ImageBlock.displayName = 'ImageBlock';

export default ImageBlock; 